import type {
  EmailRecipient,
  Fryer,
  FryerTypeRules,
  PublicStoreConfig,
  StoreConfig,
  StoreNotificationConfig,
  StoreOwnerConfig
} from "./types.js";

export const DEFAULT_STORE_CODE = "CFA00000";

export const DEFAULT_TYPE_RULES: FryerTypeRules = {
  "Pressure Fryer": { neededDays: 22, overdueDays: 28 },
  "Open Single Fryer": { neededDays: 22, overdueDays: 28 },
  "Open Double Fryer": { neededDays: 22, overdueDays: 28 }
};

export const DEFAULT_CONFIG: StoreConfig = {
  usersForEmailAlerts: [],
  typeRules: DEFAULT_TYPE_RULES,
  alertSettings: { overdueReminderDays: 0 },
  storeStatus: { isLive: false },
  featureFlags: { emailAlertsLive: false, testingMode: false }
};

function finiteNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

export function normalizeStoreCode(value: string | null | undefined): string {
  const normalized = String(value ?? "").trim().toUpperCase();
  return normalized || DEFAULT_STORE_CODE;
}

export function normalizeRecipient(value: unknown): EmailRecipient | null {
  if (typeof value === "string") {
    const email = value.trim().toLowerCase();
    if (!email) return null;
    return {
      name: "",
      email,
      alertsEnabled: true,
      assignmentAlerts: true,
      overdueAlerts: true
    };
  }

  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<EmailRecipient>;
  const email = String(raw.email ?? "").trim().toLowerCase();
  if (!email) return null;

  return {
    name: String(raw.name ?? "").trim(),
    email,
    alertsEnabled: raw.alertsEnabled !== false,
    assignmentAlerts: raw.assignmentAlerts !== false,
    overdueAlerts: raw.overdueAlerts !== false
  };
}

export function normalizeConfig(value: unknown): StoreConfig {
  const raw = record(value);
  const nickname = typeof raw.nickname === "string" ? raw.nickname.trim() : "";
  const storeContactEmail = typeof raw.storeContactEmail === "string"
    ? raw.storeContactEmail.trim().toLowerCase()
    : "";
  const rawAlertSettings = raw.alertSettings && typeof raw.alertSettings === "object"
    ? raw.alertSettings as Record<string, unknown>
    : {};
  const rawStoreStatus = raw.storeStatus && typeof raw.storeStatus === "object"
    ? raw.storeStatus as Record<string, unknown>
    : {};
  const rawFeatureFlags = raw.featureFlags && typeof raw.featureFlags === "object"
    ? raw.featureFlags as Record<string, unknown>
    : {};
  const recipients = Array.isArray(raw.usersForEmailAlerts)
    ? raw.usersForEmailAlerts.map(normalizeRecipient).filter((item): item is EmailRecipient => Boolean(item))
    : [];

  return {
    ...(nickname ? { nickname } : {}),
    ...(storeContactEmail ? { storeContactEmail } : {}),
    usersForEmailAlerts: recipients,
    typeRules: raw.typeRules && typeof raw.typeRules === "object"
      ? raw.typeRules as FryerTypeRules
      : DEFAULT_TYPE_RULES,
    alertSettings: {
      overdueReminderDays: Math.max(0, finiteNumber(rawAlertSettings.overdueReminderDays, 0))
    },
    storeStatus: {
      isLive: rawStoreStatus.isLive === true
    },
    featureFlags: {
      emailAlertsLive: rawFeatureFlags.emailAlertsLive === true,
      testingMode: rawFeatureFlags.testingMode === true
    },
    ...(typeof raw.stripeSubscriptionId === "string" ? { stripeSubscriptionId: raw.stripeSubscriptionId } : {}),
    ...(typeof raw.stripeCustomerId === "string" ? { stripeCustomerId: raw.stripeCustomerId } : {})
  };
}

export function publicStoreConfigDocument(config: StoreConfig): PublicStoreConfig {
  return {
    typeRules: config.typeRules,
    alertSettings: config.alertSettings,
    storeStatus: config.storeStatus,
    featureFlags: config.featureFlags
  };
}

export function storeNotificationConfigDocument(config: StoreConfig): StoreNotificationConfig {
  return { usersForEmailAlerts: config.usersForEmailAlerts };
}

export function storeOwnerConfigDocument(config: StoreConfig): StoreOwnerConfig {
  return {
    ...(config.nickname ? { nickname: config.nickname } : {}),
    ...(config.storeContactEmail ? { storeContactEmail: config.storeContactEmail } : {}),
    ...(config.stripeSubscriptionId ? { stripeSubscriptionId: config.stripeSubscriptionId } : {}),
    ...(config.stripeCustomerId ? { stripeCustomerId: config.stripeCustomerId } : {})
  };
}

/**
 * Reads the split configuration model while retaining a legacy fallback until
 * every production store has been migrated. A private document always wins,
 * including an intentionally empty recipient list.
 */
export function mergeStoreConfigDocuments(
  publicValue: unknown,
  notificationValue?: unknown,
  ownerValue?: unknown
): StoreConfig {
  const publicRaw = record(publicValue);
  const notificationRaw = record(notificationValue);
  const ownerRaw = record(ownerValue);
  const notifications = Object.hasOwn(notificationRaw, "usersForEmailAlerts")
    ? notificationRaw
    : publicRaw;

  return normalizeConfig({
    ...publicRaw,
    usersForEmailAlerts: notifications.usersForEmailAlerts,
    nickname: Object.hasOwn(ownerRaw, "nickname") ? ownerRaw.nickname : publicRaw.nickname,
    storeContactEmail: Object.hasOwn(ownerRaw, "storeContactEmail")
      ? ownerRaw.storeContactEmail
      : publicRaw.storeContactEmail,
    stripeSubscriptionId: Object.hasOwn(ownerRaw, "stripeSubscriptionId")
      ? ownerRaw.stripeSubscriptionId
      : publicRaw.stripeSubscriptionId,
    stripeCustomerId: Object.hasOwn(ownerRaw, "stripeCustomerId")
      ? ownerRaw.stripeCustomerId
      : publicRaw.stripeCustomerId
  });
}

export function normalizeFryer(id: string, value: unknown): Fryer {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return {
    id,
    name: String(raw.name ?? `Fryer ${id}`),
    type: String(raw.type ?? "Pressure Fryer"),
    lastBoilOut: String(raw.lastBoilOut ?? ""),
    needsBoilOut: raw.needsBoilOut === true,
    needsReason: String(raw.needsReason ?? ""),
    needsNotes: String(raw.needsNotes ?? ""),
    history: Array.isArray(raw.history) ? raw.history as Fryer["history"] : [],
    ...(raw.emailAlertState && typeof raw.emailAlertState === "object"
      ? { emailAlertState: raw.emailAlertState as Fryer["emailAlertState"] }
      : {})
  };
}

export function storeAllowsAccess(config: StoreConfig): boolean {
  return config.storeStatus.isLive || config.featureFlags.testingMode;
}
